// src/content/prices.ts
//
// Shop price constants + rarity drop weights.
//
// Phase 2: FICHE_PRICES / BALL_PRICES / RELIC_PRICES (3-tier).
// Phase 12 (FICHE-07/08/09): RARITY_WEIGHTS table (4-tier 60/25/12/3) +
//   rollRarityForAnte() helper that filters `legendary` for ante 1 and
//   renormalizes. Per PITFALLS.md Pitfall 11. Per CONTEXT.md locked decisions.
//
// Note on prices: legendary price added in Phase 14 when `chess_queen` landed.

import type { Rarity } from '../state/types';
import type { Rng } from '../systems/rng';

// Phase 14: `legendary` tier added for chess_queen. $16 is the midpoint of
// the $14–$18 band specified in 14-CONTEXT.md (Claude's Discretion).
export const FICHE_PRICES = { common: 3, uncommon: 5, rare: 8, legendary: 16 } as const;
export const BALL_PRICES = { common: 4, uncommon: 6, rare: 10 } as const;
export const RELIC_PRICES = { common: 5, uncommon: 8, rare: 12 } as const;

// Phase 15 (CONSUMABLE-07): consumable shop pricing.
export const CONSUMABLE_PRICES = { common: 4, uncommon: 7, rare: 11 } as const;

/**
 * 4-tier rarity drop weights. Sum = 100.
 * - common 60: workhorse tier; deck-building feels achievable
 * - uncommon 25: middle ground (unchanged from v1)
 * - rare 12: felt as "rare but achievable" (up from v1's 5)
 * - legendary 3: "you're lucky to see one" — preserves wow factor; ante-gated
 */
export const RARITY_WEIGHTS = {
  common: 60,
  uncommon: 25,
  rare: 12,
  legendary: 3,
} as const;

/**
 * Pull a rarity respecting ante-gating: `legendary` is excluded for ante 1.
 * Effective weights for ante 1: common 60, uncommon 25, rare 12 (sum 97);
 * picks renormalize over the reduced total.
 *
 * Deterministic: consumes exactly one `rng()` call per invocation.
 *
 * Optional `available` set further restricts the candidate tiers — used by
 * `ShopScene` to filter out tiers with empty catalogue pools (e.g. legendary
 * has no v1 entries) so the helper never returns a rarity the caller cannot
 * satisfy. This keeps RNG consumption invariant: exactly one `rng()` call
 * per invocation, regardless of catalogue contents. Without this, callers
 * had to loop-and-reroll on empty pools, which drained the shared run RNG
 * non-deterministically and shifted downstream wheel/spin outcomes (HI-01
 * from 12-REVIEW.md).
 *
 * If `available` excludes every tier the ante would otherwise allow, the
 * helper falls back to the unfiltered tier list — defensive only; callers
 * must ensure at least one tier is reachable.
 */
export function rollRarityForAnte(
  rng: Rng,
  ante: 1 | 2 | 3,
  available?: ReadonlySet<Rarity>,
): Rarity {
  const baseTiers: { rarity: Rarity; weight: number }[] =
    ante === 1
      ? [
          { rarity: 'common', weight: RARITY_WEIGHTS.common },
          { rarity: 'uncommon', weight: RARITY_WEIGHTS.uncommon },
          { rarity: 'rare', weight: RARITY_WEIGHTS.rare },
        ]
      : [
          { rarity: 'common', weight: RARITY_WEIGHTS.common },
          { rarity: 'uncommon', weight: RARITY_WEIGHTS.uncommon },
          { rarity: 'rare', weight: RARITY_WEIGHTS.rare },
          { rarity: 'legendary', weight: RARITY_WEIGHTS.legendary },
        ];
  const filtered = available
    ? baseTiers.filter((t) => available.has(t.rarity))
    : baseTiers;
  // Defensive fallback: if the filter eliminates every tier, use the base
  // ante-gated set so we still consume exactly one rng() call.
  const tiers = filtered.length > 0 ? filtered : baseTiers;
  const total = tiers.reduce((s, t) => s + t.weight, 0);
  let r = rng() * total;
  for (const t of tiers) {
    r -= t.weight;
    if (r <= 0) return t.rarity;
  }
  return tiers[tiers.length - 1]!.rarity;
}
